import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TrainingSetup from './TrainingSetup';
import TrainingCard from './TrainingCard';
import { techniquesData, comingSoonGroups } from '../../data/techniques';
import { BELT_IDS } from '../../data/belts';
import { useAuth } from '../../context/AuthContext';
import { track, trackTechnique } from '../../services/activityService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiAward, FiRefreshCw } = FiIcons;

const BELT_ORDER = BELT_IDS;

export default function TrainingView({ onExit, onShowVideo }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isFinished, setIsFinished] = useState(false);

  const startTraining = (config) => {
    let pool = [];
    const selectedIndex = BELT_ORDER.indexOf(config.selectedBelt);
    const applicableBelts = BELT_ORDER.slice(0, selectedIndex + 1);
    
    const typesToInclude = config.type === 'all' ? ['secuencias', 'formas'] : [config.type];
    
    typesToInclude.forEach(t => {
      const hidden = comingSoonGroups[t] || [];
      const items = (techniquesData[t] || []).filter(item => !hidden.includes(item.group));
      const filtered = items.filter(item => applicableBelts.includes(item.beltType));
      pool = [...pool, ...filtered];
    });

    if (config.isRandom) {
      pool = [...pool].sort(() => Math.random() - 0.5);
    } else {
      pool = [...pool].sort((a, b) => {
        return BELT_ORDER.indexOf(a.beltType) - BELT_ORDER.indexOf(b.beltType);
      });
    }

    setQueue(pool);
    setSession({ ...config, total: pool.length, startedAt: Date.now() });
    track('entrenamiento_inicio', {
      module: 'entrenamiento',
      details: { grado: config.selectedBelt, tipo: config.type, aleatorio: config.isRandom, total: pool.length },
    });
    setIsFinished(false);

    // RESET SCROLL TO TOP WHEN STARTING
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDone = (id) => {
    const newQueue = queue.filter(item => item.id !== id);
    trackTechnique('entrenamiento_tecnica', queue.find(item => item.id === id), { resultado: 'hecho' });
    setQueue(newQueue);
    if (newQueue.length === 0) {
      setIsFinished(true);
      track('entrenamiento_fin', {
        module: 'entrenamiento',
        details: {
          grado: session.selectedBelt,
          tipo: session.type,
          total: session.total,
          segundos: Math.round((Date.now() - session.startedAt) / 1000),
        },
      });
    }
  };

  const handleReviewLater = (tech) => {
    trackTechnique('entrenamiento_tecnica', tech, { resultado: 'repasar_despues' });
    const remaining = queue.filter(item => item.id !== tech.id);
    setQueue([...remaining, tech]);
  };

  if (!session) {
    return <TrainingSetup onStart={startTraining} userBelt={user?.belt} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <button 
          onClick={() => {
            setSession(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} 
          className="group flex items-center gap-2 text-white hover:text-red-500 transition-colors text-[10px] font-black uppercase tracking-[0.3em]"
        >
          <SafeIcon icon={FiArrowLeft} /> Cambiar Configuración
        </button>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-white/5 rounded-2xl border border-white/5 text-[9px] font-black uppercase tracking-[0.2em] text-white/80">
            Filtro: <span className="text-white ml-1">{session.type === 'all' ? 'Todo' : session.type}</span>
          </div>
          <div className="px-5 py-2 bg-red-600/10 rounded-2xl border border-red-600/20 text-[9px] font-black uppercase tracking-[0.2em] text-red-500 flex items-center gap-2">
            Restantes <span className="bg-red-600 text-white px-2 py-0.5 rounded-md text-[10px]">{queue.length}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <AnimatePresence mode="popLayout" initial={false}>
          {isFinished ? (
            <motion.div 
              key="finished"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-20 text-center glass-card rounded-[3rem] p-12"
            >
              <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center text-green-500 mb-8 border border-green-500/30">
                <SafeIcon icon={FiAward} className="w-12 h-12" />
              </div>
              <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4 text-white">¡Rutina Completada!</h2>
              <p className="text-white uppercase tracking-[0.3em] text-[10px] font-bold mb-12">Has mantenido tu técnica al día.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => {
                    setSession(null);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} 
                  className="px-8 py-4 bg-white/5 hover:bg-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white transition-all border border-white/5"
                >
                  Nueva Configuración
                </button>
                <button 
                  onClick={() => startTraining(session)}
                  className="px-8 py-4 bg-red-600 hover:bg-red-500 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white transition-all shadow-xl shadow-red-600/20 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} /> Repetir Todo
                </button>
              </div>
            </motion.div>
          ) : queue.length > 0 ? (
            queue.map((tech) => (
              <TrainingCard 
                key={tech.id} 
                technique={tech} 
                onDone={handleDone} 
                onReviewLater={handleReviewLater}
                onShowVideo={onShowVideo}
              />
            ))
          ) : (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
              <p className="text-white uppercase tracking-[0.3em] text-[10px] font-bold">No se encontraron técnicas.</p>
              <button onClick={() => setSession(null)} className="mt-8 text-red-500 font-black uppercase text-[10px] tracking-widest hover:underline">Cambiar parámetros</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}